import {ChangeDetectionStrategy, Component, Inject, Injectable, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {AgendaelementId, EnkelGruppeModell, GruppeId, MøteId, MøteModellEnkel, MøteRessursClient, SakFlyttetModell, TynnMøteModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {MøteTilgjengelighetForVelgMøterKomponent, MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN, VelgMøterKomponent} from "../../../møte/komponenter/velg-møter/velg-møter";
import {tomAgendaelementModell, tomGruppeModell} from "felles-ng2/modell/tommeModeller";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {AgendaelementModellDLM, SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {MøteTilgjengelighetForVelgMøterKomponentForKopierMøte} from "web-ng2/moduler/sak/modale/kopier-sak-dialog/kopier-sak-dialog";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";

/** Sann dersom bruker flyttet saken, false dersom bruker avbrøt */
export type FlyttSakDialogRespons = boolean;

const debug = initDebugger('FlyttSakDialogKomponent');

/**
 * Dialog for å flytte sak til annet møte
 */
@Component({
    selector: 'flytt-sak-dialog',
    templateUrl: 'flytt-sak-dialog.html',
    styleUrl: 'flytt-sak-dialog.scss',
    providers: [{provide: MøteTilgjengelighetForVelgMøterKomponent_DI_TOKEN, useFactory: (komponent: FlyttSakDialogKomponent) => komponent.møteTilgjengelighetForVelgMøterKomponent(), deps: [FlyttSakDialogKomponent]}],
    imports: [
        FormaterZonedDateTimePipe,
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatLabel,
        MatToolbar,
        ReactiveFormsModule,
        SubmitButtonKomponent,
        TranslateModule,
        VelgMøterKomponent,
        TipsKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlyttSakDialogKomponent {

    static vis(dialog: MatDialog, sak: Promise<AgendaelementModellDLM>): MatDialogRef<FlyttSakDialogKomponent, FlyttSakDialogRespons> {
        return dialog.open(FlyttSakDialogKomponent, {
            data: {sak} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'flytt-sak-dialog-tittel',
            minHeight: 400,
            width: '600px'
        });
    }

    skjema = new FormGroup({
        moete: new FormControl<MøteModellEnkel | null>(null, {nonNullable: true, validators: Validators.required}),
    });

    sak = signal<AgendaelementModellDLM>(tomAgendaelementModell());
    kopiertTilMoete = signal<KopiertTilMøte | null>(null);
    gruppe = signal<EnkelGruppeModell>(tomGruppeModell());
    modus = signal<'skjema' | 'kvittering'>('skjema');
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: FlyttSakDialogKomponentAvhengigheter,
                public dialogRef: MatDialogRef<FlyttSakDialogKomponent, FlyttSakDialogRespons>,
                private navigasjon: Navigasjon,
                private oversettelser: Oversettelser,
                private kontekst: BrukerkontekstTilbyder) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.initSak(parametre.sak);
    }

    gaaTilSak() {
        this.navigasjon.gåTilMøteOgSak(this.kopiertTilMoete()!.moete.id, this.kopiertTilMoete()!.sak.id);
    }

    async flytt() {
        await this.lagrer.utfør(async () => {
            //navigerer til møte (før dette står vi på sakssiden) for å unngå visning av 'Forespurt sak eksisterer ikke' melding via SakerKontroller
            this.navigasjon.gåTilMøte(this.sak().moete.id);

            const resultat = await this.avhengigheter.flytt(this.sak().id, this.skjema.value.moete!.id);

            this.kopiertTilMoete.set({moete: resultat.nyttMoete, sak: resultat.flyttetSak, gruppe: this.finnGruppe(resultat.flyttetSak.eiendeGruppeId)});
            this.modus.set('kvittering');
        });
    }

    avbryt() {
        this.dialogRef.close(false);
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    møteTilgjengelighetForVelgMøterKomponent(): MøteTilgjengelighetForVelgMøterKomponent {
        return new MøteTilgjengelighetForVelgMøterKomponentForFlyttMøte(this.oversettelser, this.sak, gruppeId => this.avhengigheter.finnSenesteLåsteMøte(gruppeId));
    }

    // private hjelpemetoder

    private async initSak(sakpromise: Promise<AgendaelementModellDLM>) {
        this.sak.set(await this.laster.utfør(() => sakpromise));
        this.gruppe.set(this.finnGruppe(this.sak().eiendeGruppeId));
    }

    private finnGruppe(gruppeId: GruppeId): EnkelGruppeModell {
        return this.kontekst.autentisertBruker().finnAutentiserteGruppe(gruppeId)!;
    }
}

/** {@link MøteTilgjengelighetForVelgMøterKomponent} implementasjon som signaliserer at låste møter ikke kan velges i nedtrekksmenyen til {@link VelgMøterKomponent}. */
class MøteTilgjengelighetForVelgMøterKomponentForFlyttMøte extends MøteTilgjengelighetForVelgMøterKomponentForKopierMøte {

    constructor(oversettelser: Oversettelser, sakSomSignal: Signal<AgendaelementModellDLM>, finnSenesteLåsteMøte: (gruppeId: GruppeId) => Promise<MøteModellEnkel>) {
        super(oversettelser, sakSomSignal, finnSenesteLåsteMøte);
    }

    override async erMøteTilgjengeligForValg(møte: MøteModellEnkel): Promise<boolean> {
        //ingen vits å flytte saken til møtet saken allerede er knyttet til
        if (this.erTilknyttetSak(møte)) return Promise.resolve(false);
        return super.erMøteTilgjengeligForValg(møte);
    }

    override årsak(møte: MøteModellEnkel): string {
        if (this.erTilknyttetSak(møte)) return this.oversettelser.oversett(i18n('Saken er knyttet til dette møtet'));
        return super.årsak(møte);
    }

    private erTilknyttetSak(møte: MøteModellEnkel) {
        return møte.id === this.sak().moete.id;
    }
}


export interface DialogParametre {
    sak: Promise<AgendaelementModellDLM>;
}

interface KopiertTilMøte {
    sak: AgendaelementModellDLM;
    moete: TynnMøteModell;
    gruppe: EnkelGruppeModell;
}

@Injectable()
export class FlyttSakDialogKomponentAvhengigheter {

    constructor(private møteRessurs: MøteRessursClient, private sakDatatjeneste: SakDatatjeneste) {
    }

    finnSenesteLåsteMøte(gruppeId: GruppeId): Promise<MøteModellEnkel> {
        return this.møteRessurs.finnSenesteLåsteMøte({gruppeId: gruppeId}).somPromise();
    }

    flytt(kildesakId: AgendaelementId, målMøteId: MøteId): Promise<SakFlyttetModell> {
        return this.sakDatatjeneste.flyttTilMøte(kildesakId, målMøteId);
    }
}
